<template>
  <!-- One entry in the /brand-colors index: logo, name, tagline and a strip of
       the brand's colours. The whole card is a link (the <span> overlay keeps a
       single, readable anchor for crawlers). -->
  <BaseCard as="li" variant="tile" padding="none" interactive class="relative flex flex-col overflow-hidden">
    <!-- Most of these assets are the brand's dark-mode logo, i.e. white ink,
         which is invisible on a white tile. `logoLight` is precomputed into the
         dataset (scripts/detect-logo-tone.mjs) so the tile can flip to dark. -->
    <div class="flex items-center justify-center px-6 py-8" :class="brand.logoLight ? 'bg-gray-800' : 'bg-white'">
      <!-- Fixed box rather than `w-auto`: these logos are third-party assets of
           every shape, and until one loads the browser sizes it from the
           width/height attributes (180x48 -> 150px wide at h-10). Almost none
           are that shape, so the box snapped to its real width on load and the
           logo jumped sideways. `object-contain` in a fixed 180x40 box renders
           the logo at exactly the same size, centred, but the box never
           changes — so there is nothing to shift. -->
      <img
        v-if="brand.logo"
        :src="brand.logo"
        :alt="`${brand.name} logo`"
        width="180"
        height="48"
        loading="lazy"
        decoding="async"
        class="h-10 w-[180px] max-w-full object-contain"
      />
      <span v-else class="text-lg font-bold" :class="brand.logoLight ? 'text-white' : 'text-gray-900'">{{ brand.name }}</span>
    </div>

    <div class="flex flex-1 flex-col p-5">
      <BaseHeading size="item">
        <!-- The overlay makes the whole card the link's hit area, so it also
             carries the link's focus ring — around the card, not the words. -->
        <NuxtLink :to="`/brand-colors/${brand.slug}`" class="group/link focus-visible:outline-none" @click="trackOpen">
          <!-- Inset: the card clips its overflow, which would cut an outer ring. -->
          <span class="absolute inset-0 rounded-xl group-focus-visible/link:outline group-focus-visible/link:outline-2 group-focus-visible/link:-outline-offset-2 group-focus-visible/link:outline-indigo-500" aria-hidden="true"></span>
          {{ brand.name }} brand colors
        </NuxtLink>
      </BaseHeading>
      <p class="mt-1 text-xs text-gray-500">{{ brand.domain }}</p>
      <p v-if="brand.tagline" class="mt-2 line-clamp-2 text-sm leading-6 text-gray-400">{{ brand.tagline }}</p>

      <div class="mt-4 flex flex-wrap gap-1.5" aria-hidden="true">
        <span
          v-for="color in brand.brandColors"
          :key="color.value"
          class="h-6 w-6 rounded-md ring-1 ring-inset ring-white/20"
          :style="{ backgroundColor: color.value }"
          :title="color.hex || color.value"
        ></span>
      </div>
      <p class="sr-only">{{ hexList }}</p>
    </div>
  </BaseCard>
</template>

<script setup>
import { computed } from 'vue'
import { trackEvent } from '~/composables/analytics'
import BaseCard from '~/components/atoms/BaseCard.vue'
import BaseHeading from '~/components/atoms/BaseHeading.vue'

const props = defineProps({
  // A BrandSummary from server/utils/brands.ts.
  brand: { type: Object, required: true },
})

// The swatch strip is decorative; the codes themselves still need to be in the
// text for search engines (and screen readers).
const hexList = computed(() =>
  props.brand.brandColors.map((color) => color.hex || color.value).join(', '),
)

// Which brands the catalog actually sends people to — the counterpart to the
// search bar's "Brand Search Result Opened", which covers the other route in.
const trackOpen = () => {
  try {
    trackEvent('Brand Card Opened', { brand: props.brand.slug, domain: props.brand.domain })
  } catch {}
}
</script>
